import { makeStyles } from "@mui/styles";
import { colors } from "../../../styles/colors";
import { down, hoverOnly, reducedMotion } from "../../../styles/breakpoints";

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    },
  },

  dialogTitle: {
    borderBottom: `1px solid ${colors.default}`,
    padding: '20px 24px',
    backgroundColor: colors.bgcolor,
  },

  dialogContent: {
    marginTop: '24px',
    padding: '24px',

    [down.sm]: {
      padding: '16px',
    },
  },
  
  dialogActions: {
    borderTop: `1px solid ${colors.default}`,
    padding: '16px 24px',
    gap: '12px',

    [down.sm]: {
      padding: '12px 16px',
      flexDirection: 'column-reverse',

      '& button': {
        width: '100%',
      },
    },
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
    paddingBottom: '12px',
    borderBottom: `2px solid ${colors.primary}`,
    
    '& h6': {
      color: colors.BLACK,
      fontSize: '18px',
      fontWeight: 600,
    },
  },
  
  sectionTitle: {
    marginTop: '16px',
    color: colors.BLACK,
    fontWeight: 600,
  },
  
  imageUploadContainer: {
    position: 'relative',
    display: 'inline-block',
  },

  removeImageButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: colors.WHITE,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',

    [hoverOnly]: {
      '&:hover': {
        backgroundColor: colors.error,

        '& svg': {
          '& path': {
            fill: colors.WHITE,
          },
        },
      },
    },
  },
  
  /*
   * A wrapping flex row of fixed 150px tiles left a ragged gap on the right at
   * most widths. An auto-fill grid divides the row evenly instead, and the
   * tiles shrink on a narrow phone rather than dropping to one per line.
   */
  galleryContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))',
    gap: '16px',
    marginTop: '12px',

    [down.sm]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(min(110px, 100%), 1fr))',
      gap: '12px',
    },
  },

  galleryImageCard: {
    position: 'relative',
    /* Was a fixed 150x150; the grid column now sets the width and the square
       shape is kept by the aspect ratio. */
    width: '100%',
    aspectRatio: '1 / 1',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',

    [reducedMotion]: {
      transition: 'none',
    },

    /*
     * The remove button was revealed on hover only. On a touch screen there is
     * no hover, so on a phone the delete control was unreachable — the actions
     * bar is now always visible there.
     */
    [hoverOnly]: {
      '& $galleryImageActions': {
        opacity: 0,
      },
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',

        '& $galleryImageActions': {
          opacity: 1,
        },
      },
    },
  },

  galleryImageActions: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
    /* Visible by default; `hoverOnly` above hides it where hover exists. */
    opacity: 1,
    transition: 'opacity 0.2s',
    [reducedMotion]: {
      transition: 'none',
    },
  },

  galleryRemoveButton: {
    backgroundColor: colors.error,
    padding: '6px',

    [hoverOnly]: {
      '&:hover': {
        backgroundColor: colors.tertiary,
      },
    },
  },

  addGalleryImage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    aspectRatio: '1 / 1',
    border: `2px dashed ${colors.greydark}`,
    borderRadius: '8px',
    backgroundColor: colors.bgcolor,
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',

    [reducedMotion]: {
      transition: 'none',
    },
    [hoverOnly]: {
      '&:hover': {
        borderColor: colors.primary,
        backgroundColor: colors.lightBlue,
      },
    },
  },
  
  galleryInputContainer: {
    display: "flex",
    gap: '8px',
    marginBottom: '8px',
    alignItems: 'flex-start',

    [down.sm]: {
      flexDirection: 'column',

      '& button': {
        width: '100%',
      },
    },
  },
  
  galleryChipsContainer: {
    display: "flex",
    gap: '8px',
    flexWrap: "wrap",
    marginTop: '12px',
  },
}));

export default useStyles;